<template>
	<Teleport :to="overlayTarget" :disabled="!isFullscreen || !overlayTarget">
		<v-card ref="cardRef" v-bind="$attrs" class="chart-card" :class="{ 'chart-card--fill-height': fillHeight }" flat>
			<v-card-title class="chart-card__title">
				<div class="chart-card__title-row">
					<div class="chart-card__title-text">
						<v-icon v-if="icon">{{ icon }}</v-icon>
						{{ title }}
					</div>

					<div class="chart-card__toolbar">
						<div v-if="$slots.subheader" class="chart-card__subheader">
							<slot name="subheader" />
						</div>
						<slot name="actions" />
						<template v-if="!hideActions">
							<v-tooltip :attach="overlayTarget" location="bottom">
								<template #activator="{ props: tooltipProps }">
									<v-btn v-bind="tooltipProps" icon="download" variant="tonal" density="comfortable" :disabled="isEmpty" @click="download" />
								</template>
								<span>{{ t('Download') }}</span>
							</v-tooltip>
							<v-tooltip :attach="overlayTarget" location="bottom">
								<template #activator="{ props: tooltipProps }">
									<v-btn v-bind="tooltipProps" :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'" variant="tonal" density="comfortable" @click="toggleFullscreen" />
								</template>
								<span>{{ isFullscreen ? t('ExitFullscreen') : t('Fullscreen') }}</span>
							</v-tooltip>
						</template>
					</div>
				</div>
			</v-card-title>

			<v-card-text class="chart-card__body">
				<div v-show="!isEmpty" class="chart-card__plot" :class="{ 'chart-card__plot--split': $slots.aside, 'chart-card__plot--scrollable': scrollable }">
					<div ref="containerRef" class="chart-card__canvas" :style="{ ...plotHeightStyle, ...canvasWidthStyle }"></div>
					<div v-if="$slots.aside" class="chart-card__aside">
						<slot name="aside" />
					</div>
				</div>
				<div v-if="isEmpty" class="chart-card__empty" :style="plotHeightStyle">
					<v-icon size="32">bar_chart</v-icon>
					<span>{{ emptyLabel || t('NoData') }}</span>
				</div>
			</v-card-text>
		</v-card>
	</Teleport>
</template>

<script setup>
import { ref, inject, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { ensureAgChartsRegistered, AgCharts, CHART_THEME } from '@/utils/agGrid/agCharts';
import { log } from '@/utils/core/vueLogger.js';

// Automatic $attrs fallthrough doesn't reliably reach the real card once it's wrapped in <Teleport> -
// bound explicitly on the card below instead (see the `v-bind="$attrs"` there).
defineOptions({ inheritAttrs: false });

const props = defineProps({
	title: { type: String, default: '' },
	icon: { type: String, default: '' },
	options: { type: Object, default: null },
	height: { type: Number, default: 320 },
	isEmpty: { type: Boolean, default: false },
	emptyLabel: { type: String, default: '' },
	fileName: { type: String, default: 'chart' },
	hideActions: { type: Boolean, default: false },
	fillHeight: { type: Boolean, default: false },
	scrollable: { type: Boolean, default: false },
	minCategoryWidth: { type: Number, default: 72 },
});

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);

const cardRef = ref(null);
const containerRef = ref(null);
const isFullscreen = ref(false);

let chart = null;

const chartOptions = computed(() => (props.options ? { theme: CHART_THEME, ...props.options, container: containerRef.value } : null));
const plotHeightStyle = computed(() => (props.fillHeight ? {} : { height: `${props.height}px` }));
const categoryCount = computed(() => (Array.isArray(props.options?.data) ? props.options.data.length : 0));
const canvasWidthStyle = computed(() => (props.scrollable && categoryCount.value > 0 ? { minWidth: `${categoryCount.value * props.minCategoryWidth}px` } : {}));

async function render() {
	if (props.isEmpty || !containerRef.value || !chartOptions.value) return;

	try {
		ensureAgChartsRegistered();
		if (chart) {
			await chart.update(chartOptions.value);
			return;
		}
		chart = AgCharts.create(chartOptions.value);
	} catch (error) {
		log('error', 'ChartCard', `❌ Chart render failed (${props.title}): ${error.message}`);
	}
}

function destroyChart() {
	try {
		chart?.destroy();
	} catch (error) {
		log('warn', 'ChartCard', `⚠️ Chart destroy failed: ${error.message}`);
	}
	chart = null;
}

watch(
	() => props.isEmpty,
	(empty) => {
		if (empty) destroyChart();
		else nextTick(render);
	}
);

watch(() => props.options, render, { deep: true });

onMounted(render);
onBeforeUnmount(destroyChart);

async function download() {
	try {
		await chart?.download({ fileName: props.fileName });
	} catch (error) {
		log('warn', 'ChartCard', `⚠️ Chart download failed: ${error.message}`);
	}
}

function toggleFullscreen() {
	const el = cardRef.value?.$el ?? cardRef.value;
	if (!el) return;
	isFullscreen.value = !isFullscreen.value;
	el.classList.toggle('chart-card--fullscreen', isFullscreen.value);
}

defineExpose({
	get chart() {
		return chart;
	},
	refresh: render,
});
</script>
